import React, { useState } from 'react';
import { decode } from 'jsonwebtoken';
import { Form, Input, Button, Empty, Steps } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import { useStoreActions, useStoreState } from '../store';
import { UnauthenticatedLayout } from '../layout/UnauthenticatedLayout';
import { LocalizedText } from '../components/i18n/LocalizedText';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ShipiumTag } from '../components/shared/ShipiumTag';
import { useEffect } from 'react';
import { differenceInHours } from 'date-fns';
export interface PasswordResetTokenPayload {
  userId: string;
  email: string;
  accessToken: string;
  iat: number;
}

const specialChars = `#$%@&%*?_(){}+=-^_<>/\\:;~,.!`;
const specialCharRegex = /[#$%@&%*?_(){}+=\-^_<>/\\:;~,.!]/;

const complexityRules = [
  { text: 'Length must be at least 8 characters', validator: (p: string) => p.length >= 8 },
  {
    text: 'Must contain at least one lower case letter',
    validator: (p: string) => {
      const re = new RegExp('[a-z]');
      return p.search(re) > -1;
    },
  },
  {
    text: 'Must contain at least one upper case letter',
    validator: (p: string) => {
      const re = new RegExp('[A-Z]');
      return p.search(re) > -1;
    },
  },
  {
    text: 'Must contain at least one number',
    validator: (p: string) => {
      const re = new RegExp('[0-9]');
      return p.search(re) > -1;
    },
  },
  {
    text: `Must contain at least one special character: ${specialChars}`,
    validator: (p: string) => {
      const re = new RegExp(specialCharRegex);
      return p.search(re) > -1;
    },
  },
];
export const ChangePasswordForm = () => {
  const [form] = Form.useForm();
  const changePassword = useStoreActions((actions) => actions.session.changePassword);
  const i18n = useStoreState((state) => state.i18n.activeResources);
  const changePasswordErrorMessage = useStoreState((state) => state.session.changePasswordErrorMessage);
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const [inputPassword, setInputPassword] = useState('');
  const tokenPayload = decode(token || '') as PasswordResetTokenPayload;
  const dispatchMessage = useStoreActions((actions) => actions.messages.dispatchMessage);
  useEffect(() => {
    // based on the 2 hour expiration coded in gateway potentially not
    // accurate
    if (differenceInHours(new Date(), new Date(tokenPayload.iat * 1000)) >= 2) {
      dispatchMessage({
        content: 'Link expired please request another',
        noticeType: 'error',
      });
      history.push('/forgot-password');
    }
  }, [tokenPayload, history, dispatchMessage]);
  const renderRules = (password: string, changePasswordErrorMsg?: string) => {
    return (
      <ul style={{ listStyle: 'none', marginTop: 10 }}>
        {complexityRules.map((r) => (
          <li>
            <ShipiumTag wrap={true} style={{ maxWidth: '260px' }} color={r.validator(password) ? 'Teal' : 'Coral'}>
              {r.text}
            </ShipiumTag>
          </li>
        ))}
        {changePasswordErrorMsg && (
          <li style={{ marginTop: 10 }}>
            <ShipiumTag color={'Coral'}>{changePasswordErrorMsg}</ShipiumTag>
          </li>
        )}
      </ul>
    );
  };
  const compareToFirstPassword = (rule: any, value: any, callback: (error?: string) => void) => {
    if (value && value !== form.getFieldValue('password')) {
      callback(i18n['registration.validation.passwordMismatch']);
    } else {
      callback();
    }
  };
  // leaving this + verifityEmailNotInPassword as separate concepts from the complexity rules
  // IE not part of the checklist
  const verifyASCIIChars = (rule: any, value: string, callback: (error?: string) => void) => {
    // eslint-disable-next-line
    if (/^[\x00-\x7F]*$/.test(value)) {
      return callback();
    }
    return callback(i18n['registration.validation.passwordASCII']);
  };
  const verifyPassword = (rule: any, value: string, callback: (error?: string) => void) => {
    // re-use complexity rules to block save on fail
    complexityRules.forEach((r: any) => {
      if (!r.validator(value)) {
        callback(r.text);
      }
    });
    return callback();
  };
  const verifyEmailNotInPassword = (rule: any, value: string, callback: (error?: string) => void) => {
    const { email } = form.getFieldsValue();
    const cleansedEmail = email.slice(0, email.indexOf('@'));
    if (value.includes(cleansedEmail)) {
      callback(i18n['registration.validation.passwordEmailVerify']);
    } else {
      callback();
    }
  };

  const handleSubmit = (values: any) => {
    changePassword({ ...values, token })
      .then((res) => {
        history.push('/login');
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const cardActions = [
    <Link to="/login">
      <LocalizedText token="registration.returnToLogin" />
    </Link>,
  ];

  const steps = (
    <Steps size="small" current={0}>
      <Steps.Step title={<LocalizedText token="registration.changePassword" />} icon={<UserOutlined />} />
    </Steps>
  );

  return (
    <UnauthenticatedLayout title={steps} actions={cardActions}>
      {tokenPayload && (
        <Form layout="vertical" form={form} initialValues={{ email: tokenPayload.email }} onFinish={handleSubmit}>
          <Form.Item style={{ marginBottom: 10 }} name="email" normalize={(value: string) => value.trim()}>
            <Input
              prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder={i18n['common.email']}
              readOnly
            />
          </Form.Item>
          <Form.Item label="Password requirements:" style={{ marginBottom: 10 }}>
            {renderRules(inputPassword, changePasswordErrorMessage)}
          </Form.Item>
          <Form.Item
            validateTrigger={'submit'}
            style={{ marginBottom: 10 }}
            name="password"
            rules={[
              {
                validator: verifyPassword,
              },
              {
                validator: verifyASCIIChars,
              },
              {
                validator: verifyEmailNotInPassword,
              },
            ]}
          >
            <Input
              onChange={(e) => setInputPassword(e.target.value)}
              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              type="password"
              placeholder={i18n['account.password']}
            />
          </Form.Item>
          <Form.Item
            style={{ marginBottom: 10 }}
            name="confirmPassword"
            rules={[
              {
                validator: compareToFirstPassword,
              },
              { required: true, message: <LocalizedText token={'registration.validation.passwordRequired'} /> },
            ]}
          >
            <Input
              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              type="password"
              placeholder={i18n['registration.confirmPassword']}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
              <LocalizedText token={'common.submit'} />
            </Button>
          </Form.Item>
        </Form>
      )}
      {!tokenPayload && <Empty description={<LocalizedText token="registration.expiredChangePasswordLink" />} />}
    </UnauthenticatedLayout>
  );
};

export default ChangePasswordForm;
